import '../../styles/fonts.css';
import React, { useState, useEffect } from 'react';
import { PixelButton, PixelContainer } from './PixelComponents';

interface GroupInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (newName?: string) => void;
  groupNumber: string;
  inviteCode: string;
  qrCodeData?: string;
  showOnly?: boolean;
}

const generateQRCode = (text: string, size: number = 200) => {
  const encodedText = encodeURIComponent(text);
  return `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodedText}`;
};

export const GroupInfoModal: React.FC<GroupInfoModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  groupNumber,
  inviteCode,
  qrCodeData,
  showOnly = false,
}) => {
  const [name, setName] = useState('');
  const [fullInviteLink, setFullInviteLink] = useState('');

  useEffect(() => {
    // ✅ ИСПРАВЛЕНИЕ: Генерируем ссылку если есть inviteCode
    if (isOpen && inviteCode) {
      const currentOrigin = window.location.origin;
      const inviteLink = `${currentOrigin}/group?invite=${inviteCode}`;
      setFullInviteLink(inviteLink);
      console.log('GroupInfoModal: Сгенерирована ссылка для QR:', inviteLink);
    }
  }, [isOpen, inviteCode]); // ✅ Убрали зависимость от qrCodeData

  if (!isOpen) return null;

  const handleConfirm = () => {
    onConfirm(name.trim() || undefined);
    setName('');
    onClose();
  };

  const handleClose = () => {
    setName('');
    onClose();
  };

  const copyToClipboard = async () => {
    try {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.src = generateQRCode(fullInviteLink, 180);

      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
      });


      const canvas = document.createElement('canvas');
      const padding = 30;
      const borderWidth = 4;
      const headerHeight = 60;
      const titleHeight = 35;
      const subtitleHeight = 30;
      const qrSize = 180;

      canvas.width = 320;
      canvas.height = qrSize + padding * 2 + headerHeight + titleHeight + subtitleHeight + borderWidth * 2;

      const ctx = canvas.getContext('2d');

      if (!ctx) {
        throw new Error('Не удалось создать canvas context');
      }


      try {
        await document.fonts.ready;
      } catch (e) {
        console.log('Шрифт Tiny5 не загружен, используем запасной');
      }


      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, canvas.width, canvas.height);


      ctx.strokeStyle = '#000000';
      ctx.lineWidth = borderWidth;
      ctx.strokeRect(borderWidth / 2, borderWidth / 2, canvas.width - borderWidth, canvas.height - borderWidth);


      ctx.fillStyle = '#131313';
      ctx.font = '32px Tiny5, "Courier New", monospace';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('тЁрка', canvas.width / 2, borderWidth + padding + 15);


      ctx.font = 'bold 16px "Roboto Mono", monospace';
      ctx.fillText(name.trim() || groupNumber, canvas.width / 2, borderWidth + padding + headerHeight - 5);


      ctx.font = '11px Tiny5, "Courier New", monospace';
      ctx.fillStyle = '#666666';
      ctx.fillText('ОТСКАНИРУЙТЕ ДЛЯ ПРИСОЕДИНЕНИЯ', canvas.width / 2, borderWidth + padding + headerHeight + titleHeight - 5);


      const qrX = (canvas.width - qrSize) / 2;
      const qrY = borderWidth + padding + headerHeight + titleHeight + subtitleHeight - 10;
      ctx.drawImage(img, qrX, qrY, qrSize, qrSize);

      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((blob) => {
          if (blob) resolve(blob);
          else reject(new Error('Не удалось создать blob'));
        }, 'image/png');
      });

      await navigator.clipboard.write([
        new ClipboardItem({
          'image/png': blob,
        }),
      ]);

    } catch (error) {
      console.error('Ошибка при копировании:', error);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
      }}
    >
      <PixelContainer
        style={{
          backgroundColor: '#fff',
          padding: '16px',
          textAlign: 'center',
          fontFamily: 'Tiny5, "Courier New", monospace, sans-serif',
          fontSize: '14px',
          width: '320px',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
        }}
      >
        <div>
          {showOnly ? (
            <>
              <h3 style={{ fontSize: '16px', marginBottom: '4px' }}>НАЗВАНИЕ</h3>
              <div
                style={{
                  padding: '8px 0',
                  fontFamily: 'Roboto Mono, monospace',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  color: '#131313',
                }}
              >
                {groupNumber}
              </div>
            </>
          ) : (
            <>
              <h3 style={{ fontSize: '16px', marginBottom: '4px' }}>Можно назвать</h3>
              <PixelContainer
                style={{
                  backgroundColor: '#F0F0F0',
                  padding: '4px 0',
                  height: '40px',
                }}
              >
                <input
                  type='text'
                  placeholder={groupNumber}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    textAlign: 'center',
                    fontFamily: 'Roboto Mono, monospace',
                    fontSize: '18px',
                    fontWeight: 'bold',
                    backgroundColor: 'transparent',
                    outline: 'none',
                  }}
                />
              </PixelContainer>
            </>
          )}
        </div>
        {/* ✅ ИСПРАВЛЕНИЕ: Проверяем inviteCode вместо qrCodeData */}
        {inviteCode && fullInviteLink && (
          <div
            style={{
              padding: '12px',
              borderRadius: '4px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <div style={{ fontSize: '12px', color: '#666' }}>ОТСКАНИРУЙТЕ ДЛЯ ПРИСОЕДИНЕНИЯ</div>
            <img
              src={generateQRCode(fullInviteLink, 180)}
              alt='QR Code'
              style={{
                width: '180px',
                height: '180px',
                imageRendering: 'pixelated',
              }}
            />
            <button
              onClick={copyToClipboard}
              style={{
                background: 'none',
                border: 'none',
                color: '#131313',
                cursor: 'pointer',
                fontSize: '14px',
                fontFamily: 'Tiny5, "Courier New", monospace, sans-serif',
                padding: '4px',
                fontWeight: 500,
              }}
            >
              СКОПИРОВАТЬ PNG
            </button>
            <button
              onClick={handleClose}
              style={{
                flex: 1,
                width: '120px',
                height: '32px',
                fontFamily: 'Tiny5, "Courier New", monospace, sans-serif',
                fontWeight: 500,
              }}
            >
              ОТМЕНА
            </button>
          </div>
        )}
      </PixelContainer>

      {!showOnly && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '24px',
            width: '100%',
            marginTop: '8px',
          }}
        >
          <PixelButton
            onClick={handleConfirm}
            style={{
              flex: 1,
              height: '32px',
              fontFamily: 'Tiny5, "Courier New", monospace, sans-serif',
            }}
          >
            В КОМНАТУ
          </PixelButton>
        </div>
      )}
    </div>
  );
};
